import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Github, Linkedin, Mail, Instagram, Code, Sparkles, Twitter } from 'lucide-react';
import { PORTFOLIO_DATA } from '../../utils/constants';
import './Hero.css';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentRole, setCurrentRole] = useState(0);

  const roles = [
    'Full-Stack Developer',
    'Odoo Specialist',
    'Python Developer',
    'React Developer',
    'Problem Solver'
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [-20, 20, -20],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section id="home" className="hero">
      {/* Animated Background */}
      <div className="hero-background">
        <div 
          className="hero-gradient"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(99, 102, 241, 0.3) 0%, transparent 50%)`,
          }}
        />
        <div className="hero-mesh">
          <div className="mesh-gradient mesh-1"></div>
          <div className="mesh-gradient mesh-2"></div>
          <div className="mesh-gradient mesh-3"></div>
        </div>
        <div className="hero-particles">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="hero-text">
            <motion.div variants={itemVariants} className="hero-badge">
              <Sparkles size={16} />
              <span>Available for freelance work</span>
            </motion.div>
            
            <motion.p variants={itemVariants} className="hero-greeting">
              👋 Hi there! I'm
            </motion.p>
            
            <motion.h1 variants={itemVariants} className="hero-title">
              <span className="gradient-text">{PORTFOLIO_DATA.name}</span>
            </motion.h1>
            
            <motion.div variants={itemVariants} className="hero-role">
              <h2 className="hero-subtitle">
                <span className="role-prefix">I'm a </span>
                <motion.span 
                  key={currentRole}
                  className="typing-text gradient-text"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {roles[currentRole]}
                </motion.span>
              </h2>
            </motion.div>
            
            <motion.p variants={itemVariants} className="hero-description">
              I craft exceptional digital experiences with clean code and beautiful design. 
              Passionate about building scalable applications that make a difference in the real world.
            </motion.p>

            <motion.div variants={itemVariants} className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">2+</span>
                <span className="stat-label">Years Exp</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">70+</span>
                <span className="stat-label">Projects</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">15+</span>
                <span className="stat-label">Technologies</span>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="hero-cta">
              <a href="#contact" className="btn btn-primary">
                <Mail size={20} />
                Get In Touch
              </a>
              <a href="#projects" className="btn btn-outline">
                <Code size={20} />
                View My Work
              </a>
            </motion.div>

            <motion.div variants={itemVariants} className="hero-social">
              <span className="social-label">Connect with me:</span>
              <div className="social-links">
                {Object.entries(PORTFOLIO_DATA.social).map(([platform, url]) => (
                  <motion.a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    aria-label={platform}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {platform === 'github' && <Github size={24} />}
                    {platform === 'linkedin' && <Linkedin size={24} />}
                    {platform === 'twitter' && <Twitter size={24} />}
                    {platform === 'instagram' && <Instagram size={24} />}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>
          
          <motion.div variants={itemVariants} className="hero-visual">
            <div className="hero-avatar-container">
              <motion.div 
                className="hero-avatar"
                variants={floatingVariants}
                animate="animate"
              >
                <span>{PORTFOLIO_DATA.name[0]}</span>
                <div className="avatar-glow"></div>
              </motion.div>
              
              <motion.div 
                className="avatar-ring ring-1"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="avatar-ring ring-2"
                animate={{ rotate: -360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="avatar-ring ring-3"
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              />
              
              <div className="floating-icons">
                <motion.div 
                  className="floating-icon icon-1"
                  animate={{ 
                    y: [-10, 10, -10],
                    rotate: [0, 180, 360]
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                >
                  ⚡
                </motion.div>
                <motion.div 
                  className="floating-icon icon-2"
                  animate={{ 
                    y: [10, -10, 10],
                    rotate: [0, -180, -360]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: 1
                  }}
                >
                  🚀
                </motion.div>
                <motion.div 
                  className="floating-icon icon-3"
                  animate={{ 
                    y: [-15, 15, -15],
                    rotate: [0, 90, 180]
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: 2
                  }}
                >
                  💡
                </motion.div>
                <motion.div 
                  className="floating-icon icon-4"
                  animate={{ 
                    y: [15, -15, 15],
                    rotate: [0, -90, -180]
                  }}
                  transition={{ 
                    duration: 3.5, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: 3
                  }}
                >
                  ✨
                </motion.div>
              </div>

              <div className="tech-orbits">
                <motion.div 
                  className="tech-orbit orbit-1"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                >
                  <div className="tech-icon">⚛️</div>
                </motion.div>
                <motion.div 
                  className="tech-orbit orbit-2"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                >
                  <div className="tech-icon">🐍</div>
                </motion.div>
                <motion.div 
                  className="tech-orbit orbit-3"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
                >
                  <div className="tech-icon">⚙️</div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        
        <motion.div
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <ArrowDown size={24} />
          <span>Scroll to explore</span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;